const wikiPhoto = (file) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=1600`;

const googleMaps = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const osmEmbed = (lat, lon, delta = 0.02) => {
  const minLon = lon - delta;
  const minLat = lat - delta * 0.65;
  const maxLon = lon + delta;
  const maxLat = lat + delta * 0.65;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon},${minLat},${maxLon},${maxLat}&layer=mapnik&marker=${lat},${lon}`;
};

const TRAILHEAD_QUERY =
  'Clear Creek Metro Park Creekside Meadows, 23833 Clear Creek Road, Rockbridge, OH 43149';

const PHOTOS = {
  trailhead: wikiPhoto('Cemetery Ridge Trail head (29278691062).jpg'),
  sandstone: wikiPhoto('Clear Creek Metro Park (24765352707).jpg'),
  ridgeView: wikiPhoto('Conkles Hollow.jpg'),
};

const HERO_IMAGE = PHOTOS.trailhead;

/**
 * Group-hike prep guide for Cemetery Ridge Trail at Clear Creek Metro Park.
 */
export const CEMETERY_RIDGE_HIKE_PREP_POST = {
  id: 'featured-cemetery-ridge-hike-prep',
  slug: 'cemetery-ridge-clear-creek-hike-prep',
  title: 'How to Prepare for Cemetery Ridge at Clear Creek Metro Park',
  excerpt:
    'A friends-group checklist for Cemetery Ridge: drive from Columbus, Creekside Meadows parking, pack list, the steep zigzag climb, and why this 2.5-mile trail is really an out-and-back day.',
  publishedAt: '2026-09-06T18:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Hike snapshot',
      items: [
        { value: '2.5 mi', label: 'Trail length (not a loop)' },
        { value: '~5 mi', label: 'Out-and-back if you return the same way' },
        { value: 'Mod–hard', label: 'Steep zigzag, then ridge' },
        { value: '6:30–dark', label: 'Park hours · free' },
      ],
    },
    {
      type: 'panel',
      title: 'What you are signing up for',
      paragraphs: [
        'Cemetery Ridge is the standout climb at Clear Creek Metro Park — a Columbus Metro Parks property in Rockbridge, about an hour southeast of Columbus on the way toward Hocking Hills. Metro Parks rates it **moderate to difficult**: dirt and gravel, starting with a steep zigzag through the woods that peaks at about 700 feet. On top, the trail alternates forest and meadows.',
        'It is **not a loop**. If you hike the full 2.5 miles out, you still have to come back down the same steep section (about **5 miles** total) unless you link Fern or Hemlock and know the junctions. For a first visit with friends, treat it as an out-and-back and turn around before anyone is cooked.',
        'This is not a first-ever hike. If someone in the group is new to dirt trails, walk Highbanks Overlook or Creekside Meadows first — or split the group: ridge for the climbers, creek walk for anyone who wants easy.',
      ],
      chips: ['Clear Creek Metro Park', 'Rockbridge, OH', 'Friends group', 'September 2026'],
    },
    {
      type: 'imageGallery',
      title: 'The trailhead you are looking for',
      intro:
        'Park at **Creekside Meadows**, then find the wooden **CEMETERY RIDGE TRAIL HEAD** sign. The hard climb starts just beyond it.',
      images: [
        {
          url: PHOTOS.trailhead,
          alt: 'Cemetery Ridge Trail Head wooden sign at Creekside Meadows',
          caption: 'Cemetery Ridge Trail Head — Creekside Meadows picnic area. Wikimedia Commons.',
          mapUrl: googleMaps(TRAILHEAD_QUERY),
        },
        {
          url: PHOTOS.sandstone,
          alt: 'Blackhand sandstone along Clear Creek Road',
          caption: 'Sandstone along Clear Creek Road on the drive in. Wikimedia Commons.',
          mapUrl: googleMaps(TRAILHEAD_QUERY),
        },
      ],
    },
    {
      type: 'table',
      title: 'Meet here — location details',
      headers: ['Item', 'Details'],
      rows: [
        ['Trail', 'Cemetery Ridge Trail (hike only — no bikes, no pets on this trail)'],
        ['Park', 'Clear Creek Metro Park (Columbus and Franklin County Metro Parks)'],
        ['Meeting lot', 'Creekside Meadows picnic area'],
        [
          'Address to drop in Maps',
          '23833 Clear Creek Road, Rockbridge, OH 43149',
        ],
        ['Also listed', 'Park HQ / other entrance: 185 Clear Creek Road, Lancaster, OH 43130 — do not use this if you want Cemetery Ridge'],
        ['Hours', 'Year-round, 6:30 a.m. to dark'],
        ['Fee', 'Free'],
        ['Restrooms', 'Vault restrooms at Creekside Meadows (picnic tables and grills too)'],
        ['Drive from Columbus', '~45–60 min via US 33 south; Clear Creek Rd is a marked right after the Sugar Grove area'],
        ['Drive from Powell / Polaris', '~60–75 min; same US 33 approach'],
        ['Dispatch', 'Metro Parks dispatch 614-620-1865'],
      ],
    },
    {
      type: 'mapEmbed',
      title: 'Trailhead map — Creekside Meadows',
      intro:
        'Pin this lot, not the Lancaster HQ address. Screenshot the map offline — cell service is uneven in the gorge.',
      maps: [
        {
          title: 'Creekside Meadows (Cemetery Ridge start)',
          address: '23833 Clear Creek Road, Rockbridge, OH 43149',
          embedUrl: osmEmbed(39.5915, -82.638, 0.022),
          directionsUrl: googleMaps(TRAILHEAD_QUERY),
        },
      ],
    },
    {
      type: 'panel',
      title: 'Drive plan (from Columbus)',
      orderedList: [
        'Take **US 33 south** toward Lancaster / Logan. Stay on the bypass past Carroll and the Sugar Grove light.',
        'Turn right on **Clear Creek Road** (well marked). You will pass “Leaning Lena,” a big roadside rock.',
        'Continue to **Creekside Meadows** picnic area and park. Look for picnic tables, vault restrooms, and the Cemetery Ridge Trail Head sign.',
        'Share one Google Maps pin in the group chat the night before so nobody navigates to 185 Clear Creek Rd by mistake.',
      ],
      callout: {
        text: 'Printable park map (PDF) — Metro Parks',
        url: 'https://www.metroparks.net/wp-content/uploads/2021/08/CLC_park-map.pdf',
      },
    },
    {
      type: 'panel',
      title: 'Are we ready? (honest group check)',
      list: [
        'Everyone can walk 60–90 minutes on uneven dirt without needing a bench every 10 minutes.',
        'At least two people in the car have closed-toe shoes with tread (not slides or smooth fashion sneakers).',
        'Someone is willing to turn the group around on the climb if the zigzag is too much — no “summit or nothing.”',
        'You have a weather plan: if it poured in the last day, Ohio clay on this trail gets slick and rutted. Postpone.',
        'Leave dogs at home. Pets are **not** allowed on Cemetery Ridge. The only dog stretch in the park is 1 mile of Creekside Meadows west of Starner Road.',
      ],
    },
    {
      type: 'panel',
      title: 'Pack list (one bag per person + one group kit)',
      list: [
        '**Shoes:** trail runners or hiking shoes. The first climb is steep dirt; gravel and roots after rain are the injury risk.',
        '**Water:** at least 1 liter each for the out-and-back; 1.5–2 liters if it is warm or you might add Fern/Hemlock.',
        '**Food:** a real snack (bar, sandwich, fruit). There is no store on Clear Creek Road once you are in the park.',
        '**Tick / bug:** spray on skin and clothes. Meadows on the ridge are tick country spring through fall.',
        '**Sun layer:** ridge meadows are more open than the woods. Cap + sunscreen.',
        '**Light extra layer:** the climb will soak a cotton shirt; a dry layer in the pack helps at the top.',
        '**Phone + offline map:** screenshot the Metro Parks PDF and this trailhead pin. Tell one person at home your lot and turnaround time.',
        '**Group kit (one person carries):** small first-aid (blister, bandage), extra water, and a battery pack.',
        '**Optional:** trekking poles for the descent — knees notice the zigzag more going down than up.',
      ],
    },
    {
      type: 'panel',
      title: 'Suggested Saturday with friends',
      orderedList: [
        'Night before: check Metro Parks hours, radar, and the PDF map. Pack shoes and water, not “we’ll grab it on 33.”',
        'Meet at Creekside Meadows **morning** (8–9 a.m. is cooler and leaves daylight if you are slow). Park opens 6:30 a.m.',
        'Photo at the trailhead sign, then start the zigzag. Agree on a turnaround time (for example 90 minutes out).',
        'Hike out until the ridge meadows, or until the first person wants to stop — then **turn around together**.',
        'Budget **2.5–4 hours** for a full out-and-back, plus a picnic at the Creekside tables. Be back to the cars well before dark.',
        'After: Logan / Hocking Hills is farther south if you still have legs; otherwise drive US 33 back to Columbus.',
      ],
    },
    {
      type: 'table',
      title: 'On-trail options (pick one before you leave the lot)',
      headers: ['Plan', 'Distance / time', 'Who it is for'],
      rows: [
        [
          'Short probe',
          'Climb 20–30 minutes, then back down (~1–2 mi total)',
          'Mixed fitness, or you arrived late. You still get the steep part.',
        ],
        [
          'Classic out-and-back',
          'Up to 2.5 mi out + 2.5 mi back · 2.5–4 hours',
          'Default for a friends group that wants the full ridge.',
        ],
        [
          'Link Fern or Hemlock',
          'Can stretch toward 6–8 mi if you miss a junction',
          'Only if someone has the park map and has hiked here before. Easy to overshoot.',
        ],
        [
          'Skip the ridge',
          'Creekside Meadows 1.7 mi easy grass along the creek',
          'Anyone who wants a flat walk, or a backup if the ridge is closed/muddy.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Safety notes that matter on this trail',
      list: [
        'The hard part is the **start**. Groups often blow up in the first 15 minutes. Slow down, talk, sip water.',
        'Stay on marked trail. Much of Clear Creek sits in the Allen F. Beck State Nature Preserve — off-trail is prohibited.',
        'After rain: postpone. Mud on the zigzag is slick and you will trench the trail for the next visitors.',
        'Summer heat: start early. The woods help; the ridge meadows do not.',
        'No pets, no drones drama, pack out wrappers. Vault toilets are at the picnic area, not on the ridge.',
        'This is recreation guidance, not medical advice. If someone feels faint, sit, drink, and go down — the parking lot is the recovery plan, not the ridge.',
      ],
    },
    {
      type: 'panel',
      title: 'Night-before text you can paste to the group',
      paragraphs: [
        'Cemetery Ridge · Clear Creek Metro Park. Meet **Creekside Meadows, 23833 Clear Creek Rd, Rockbridge, OH 43149**. Park 6:30 a.m.–dark, free. Closed-toe shoes, 1L+ water, snack, tick spray. Trail is 2.5 miles one way, not a loop — we turn around together. No dogs on this trail. Map PDF: metroparks.net Clear Creek page.',
      ],
    },
  ],
  relatedPostsTitle: 'More Ohio trail guides',
  relatedPosts: [
    {
      slug: 'alum-creek-best-hike-spots-beginner-moderate',
      title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
      blurb: 'closer-to-home practice before the Clear Creek climb.',
    },
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'easier first hikes if someone in the group is not ready for the ridge.',
    },
    {
      slug: 'ohio-summer-nature-kids-family-guide',
      title: 'Ohio Summer Nature Guide for Families (Ages 3–13)',
      blurb: 'other parks if you want waterfalls and less climbing.',
    },
    {
      slug: 'mountain-biking-trails-powell-dublin-polaris-delaware-ohio',
      title: 'Best Mountain Biking Trails Near Powell, Dublin, Polaris & Delaware',
      blurb: 'dirt wheels closer to home — not at Clear Creek.',
    },
  ],
  referenceLinks: [
    {
      label: 'Metro Parks — Clear Creek (hours, trails, Creekside Meadows address)',
      url: 'https://www.metroparks.net/parks-and-trails/clear-creek/',
    },
    {
      label: 'Clear Creek Metro Park printable map (PDF)',
      url: 'https://www.metroparks.net/wp-content/uploads/2021/08/CLC_park-map.pdf',
    },
    {
      label: 'Ohio beginner hiking guide (includes Cemetery Ridge as a next-step hike)',
      url: '/blog/ohio-beginner-hiking-guide',
    },
    {
      label: 'Google Maps — Creekside Meadows trailhead',
      url: googleMaps(TRAILHEAD_QUERY),
    },
  ],
};
